/**
 * Analytics Consent Banner
 * 
 * GDPR-compliant consent banner for telemetry opt-in.
 * 
 * Features:
 * - Explicit opt-in required (Fixes F001)
 * - Clear explanation of data collection
 * - Link to full privacy policy
 * - Stores consent in localStorage and user_settings
 * 
 * Shown once per user on first app launch.
 */

import React, { useState, useEffect } from 'react';
import { IonModal, IonButton, IonContent, IonHeader, IonTitle, IonToolbar, IonText } from '@ionic/react';
import { initializePostHog, shutdownPostHog } from '../../app-core/analytics/posthog';
import { initializeSentry, shutdownSentry } from '../../app-core/analytics/sentry';
import { generateDeviceId, getDeviceLabel, ANALYTICS_CONSENT_TEXT } from '../../utils/device/device-id';
import { buildAnalyticsSettingsUpdate } from '../../data/analytics/analytics-settings';

interface AnalyticsConsentBannerProps {
  onConsentGiven: (accepted: boolean) => void;
}

/**
 * Analytics Consent Banner Component
 * 
 * GDPR-compliant banner requiring explicit user consent
 */
export const AnalyticsConsentBanner: React.FC<AnalyticsConsentBannerProps> = ({ onConsentGiven }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  
  useEffect(() => {
    // Check if consent has already been given/denied
    const consentShown = localStorage.getItem('analytics_consent_shown');
    const consentValue = localStorage.getItem('analytics_consent');
    
    // Show banner if consent not yet captured
    if (!consentShown) {
      setIsOpen(true);
    }
  }, []);
  
  const handleAccept = async () => {
    // F1: Generate device ID ONLY after consent action (not before)
    const deviceId = generateDeviceId();
    const deviceLabel = getDeviceLabel();

    // Store device ID in sessionStorage for this session
    sessionStorage.setItem('device_id', deviceId);

    // F4: Build analytics settings update with audit log
    const emptySettings = { version: 0, devices: {} };
    const updatedSettings = buildAnalyticsSettingsUpdate(
      emptySettings, deviceId, true, deviceLabel,
      'banner_accept_button', ANALYTICS_CONSENT_TEXT
    );

    // Store consent locally (existing behavior)
    localStorage.setItem('analytics_consent', 'true');
    localStorage.setItem('analytics_consent_shown', 'true');

    // Store structured consent for backend sync
    localStorage.setItem('analytics_settings', JSON.stringify(updatedSettings));
    
    // Initialize telemetry
    initializePostHog();
    initializeSentry();
    
    setIsOpen(false);
    onConsentGiven(true);
  };
  
  const handleDecline = () => {
    // F1: Generate device ID ONLY after consent action (not before)
    const deviceId = generateDeviceId();
    const deviceLabel = getDeviceLabel();

    // Store device ID in sessionStorage for this session
    sessionStorage.setItem('device_id', deviceId);

    // F4: Build analytics settings update with audit log (consent declined)
    const emptySettings = { version: 0, devices: {} };
    const updatedSettings = buildAnalyticsSettingsUpdate(
      emptySettings, deviceId, false, deviceLabel,
      'banner_decline_button', ANALYTICS_CONSENT_TEXT
    );

    // Store rejection locally (existing behavior)
    localStorage.setItem('analytics_consent', 'false');
    localStorage.setItem('analytics_consent_shown', 'true');

    // Store structured consent for backend sync
    localStorage.setItem('analytics_settings', JSON.stringify(updatedSettings));
    
    // Ensure telemetry is shut down
    shutdownPostHog();
    shutdownSentry();
    
    setIsOpen(false);
    onConsentGiven(false);
  };
  
  return (
    <>
      <IonModal isOpen={isOpen} backdropDismiss={false}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>📊 Help Us Improve Loop Orienteering</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ padding: '1rem' }}>
            <IonText>
              <h2>Anonymous Usage Data</h2>
              <p>
                We collect <strong>anonymous usage data</strong> to improve the app during beta testing.
                This helps us understand which features are most useful and identify bugs faster.
              </p>
              
              <h3>What we collect:</h3>
              <ul>
                <li>Feature usage (screens visited, loops run, events joined)</li>
                <li>Crash reports and error logs</li>
                <li>Performance data (app speed, API response times)</li>
                <li>Device info (platform, OS version, app version)</li>
                <li>City/region (not precise location)</li>
              </ul>
              
              <h3>What we DON&apos;T collect:</h3>
              <ul>
                <li>❌ Email addresses or names</li>
                <li>❌ Precise GPS coordinates</li>
                <li>❌ Access tokens or passwords</li>
                <li>❌ Message content or private data</li>
              </ul>
              
              <p>
                <strong>Your privacy matters.</strong> All data is anonymized and your user ID is encrypted.
                You can opt out anytime via Settings → Privacy.
              </p>
              
              <p>
                <IonButton fill="clear" size="small" onClick={() => setShowPrivacyPolicy(true)}>
                  📄 Learn More (Full Privacy Policy)
                </IonButton>
              </p>
              
              <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <IonButton color="medium" onClick={handleDecline}>
                  Decline
                </IonButton>
                <IonButton color="primary" onClick={handleAccept}>
                  Accept & Enable Analytics
                </IonButton>
              </div>
              
              <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '1rem', textAlign: 'center' }}>
                By clicking &quot;Accept,&quot; you consent to data collection as described above.
                You can withdraw consent anytime.
              </p>
            </IonText>
          </div>
        </IonContent>
      </IonModal>
      
      {/* Privacy Policy Modal */}
      <IonModal isOpen={showPrivacyPolicy} onDidDismiss={() => setShowPrivacyPolicy(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Privacy Policy - Analytics & Error Tracking</IonTitle>
            <IonButton slot="end" fill="clear" onClick={() => setShowPrivacyPolicy(false)}>
              Close
            </IonButton>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <PrivacyPolicyContent />
        </IonContent>
      </IonModal>
    </>
  );
};

/**
 * Full Privacy Policy Content
 * 
 * Exported for reuse in Settings screen Privacy Policy modal
 */
export const PrivacyPolicyContent: React.FC = () => (
  <div style={{ padding: '1rem' }}>
    <IonText>
      <h1>Privacy Policy: Analytics & Error Tracking</h1>
      
      <p><em>Effective Date: February 8, 2026</em></p>
      
      <h2>1. Introduction</h2>
      <p>
        Loop Orienteering (&quot;we,&quot; &quot;us,&quot; &quot;our&quot;) respects your privacy and is committed to protecting your personal data.
        This privacy policy explains how we collect and use anonymous usage data to improve the app during beta testing.
      </p>
      
      <h2>2. What Data We Collect</h2>
      <p>We collect the following <strong>anonymized</strong> data with your consent:</p>
      <ul>
        <li><strong>Feature usage:</strong> Which screens you visit, loops you run, events you join</li>
        <li><strong>Performance data:</strong> App crashes, slow API responses, GPS errors</li>
        <li><strong>Device information:</strong> Platform (iOS/Android/Web), OS version, app version</li>
        <li><strong>Aggregated location:</strong> City/region (rounded to ~10km grid, not precise coordinates)</li>
      </ul>
      
      <h2>3. What We DON&apos;T Collect</h2>
      <p>We <strong>never</strong> collect:</p>
      <ul>
        <li>Email addresses or personal identifiers</li>
        <li>Precise GPS coordinates (all locations rounded to protect privacy)</li>
        <li>Access tokens, API keys, or passwords</li>
        <li>Message content or private communications</li>
        <li>IP addresses (anonymized by PostHog/Sentry)</li>
      </ul>
      
      <h2>4. How We Protect Your Privacy</h2>
      <p>We use industry-standard pseudonymization techniques:</p>
      <ul>
        <li><strong>User ID Hashing:</strong> Your Supabase user ID is encrypted using HMAC-SHA256 with a secret salt before being sent to analytics services</li>
        <li><strong>GPS Anonymization:</strong> Coordinates are rounded to 0.1° precision (~10km grid)</li>
        <li><strong>PII Validation:</strong> Automated runtime checks block any events containing email addresses, tokens, or precise coordinates</li>
        <li><strong>No Session Recording:</strong> We don&apos;t record your screen or track mouse movements</li>
      </ul>
      
      <h2>5. Third-Party Services</h2>
      <p>We use the following trusted services to process anonymized data:</p>
      <ul>
        <li>
          <strong>PostHog</strong> (product analytics): Hosted in US/EU cloud infrastructure. 
          Privacy policy: <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">https://posthog.com/privacy</a>
        </li>
        <li>
          <strong>Sentry</strong> (error tracking): Hosted in US cloud infrastructure. 
          Privacy policy: <a href="https://sentry.io/privacy" target="_blank" rel="noopener noreferrer">https://sentry.io/privacy</a>
        </li>
      </ul>
      <p>
        Both services comply with GDPR and provide data processing agreements (DPAs).
      </p>
      
      <h2>6. Your Rights (GDPR)</h2>
      <p>If you&apos;re in the EU/EEA, you have the following rights:</p>
      <ul>
        <li><strong>Right to access:</strong> Request a copy of all data we&apos;ve collected about you</li>
        <li><strong>Right to rectification:</strong> Correct inaccurate data</li>
        <li><strong>Right to erasure (&quot;right to be forgotten&quot;):</strong> Delete all your data from our systems</li>
        <li><strong>Right to data portability:</strong> Export your data in JSON format</li>
        <li><strong>Right to withdraw consent:</strong> Opt out of analytics anytime via Settings → Privacy</li>
      </ul>
      
      <h2>7. Legal Basis for Processing</h2>
      <p>
        We process your data based on <strong>your explicit consent</strong> (GDPR Article 6(1)(a)).
        You must actively click &quot;Accept&quot; to enable analytics. You can withdraw consent anytime.
      </p>
      
      <h2>8. Data Retention</h2>
      <ul>
        <li><strong>PostHog events:</strong> Retained for 90 days, then automatically deleted</li>
        <li><strong>Sentry errors:</strong> Retained for 90 days, then automatically deleted</li>
        <li><strong>Source maps:</strong> Retained for 1 year (needed for stack trace decoding)</li>
        <li><strong>Account deletion:</strong> All associated telemetry is purged within 7 days</li>
      </ul>
      
      <h2>9. How to Opt Out</h2>
      <p>You can disable analytics anytime:</p>
      <ol>
        <li>Open the app</li>
        <li>Go to <strong>Settings → Privacy</strong></li>
        <li>Toggle <strong>&quot;Share Analytics Data&quot;</strong> to OFF</li>
      </ol>
      <p>All tracking stops immediately. Existing data is retained per our retention policy.</p>
      
      <h2>10. How to Exercise Your Rights</h2>
      <p>To access, correct, or delete your data, contact us at:</p>
      <ul>
        <li><strong>Email:</strong> privacy@looporienteering.com</li>
        <li><strong>In-app:</strong> Settings → Account → Export/Delete Data</li>
      </ul>
      <p>We&apos;ll respond within 30 days as required by GDPR.</p>
      
      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this policy occasionally. Changes will be posted in-app and via email.
        Continued use of analytics after changes implies acceptance.
      </p>
      
      <h2>12. Contact Us</h2>
      <p>Questions about this privacy policy? Contact us:</p>
      <ul>
        <li><strong>Email:</strong> privacy@looporienteering.com</li>
        <li><strong>Website:</strong> https://looporienteering.com/privacy</li>
      </ul>
      
      <hr/>
      
      <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '2rem' }}>
        <strong>Last updated:</strong> February 8, 2026<br/>
        <strong>Version:</strong> 0.3.0<br/>
        <strong>GDPR Compliance:</strong> Articles 6(1)(a), 7, 13-20, 25, 32
      </p>
    </IonText>
  </div>
);

export default AnalyticsConsentBanner;
